import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
    BlobType,
    FileModel,
    FolderViewModel,
    ItemsToBeRemovedOptions,
    ListSearch,
    MEDFileAction,
    MEDFileColumnKeys,
    NameAndSelectedFilesContinueInformation,
    UploadPayload,
} from '@ca/core/models';
import { ExperimentalFeaturesService, FileService, FileUploadService, UserNotificationService } from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Guid } from 'guid-typescript';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, catchError, tap } from 'rxjs';
import { CreateFolder } from 'src/app/core/models/create-folder.model';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { UPLOADER_KEY } from '../../constants/constants';
import { CreateAndStartFacadeService } from '../create-and-start-dialog/services/create-and-start-facade/create-and-start-facade.service';
import { TableRow } from '../files-table/table-row.model';

@UntilDestroy()
@Component({
    selector: 'ca-med-upload-data-dialog',
    templateUrl: './MED-upload-data-dialog.component.html',
    styleUrls: ['./MED-upload-data-dialog.component.scss'],
    providers: [CreateAndStartFacadeService],
})
export class MEDUploadDataDialogComponent implements AfterViewInit {
    get parentFolderName(): string {
        return this._parentFolderName;
    }

    get selectedFiles(): FileModel[] {
        return this._selectedFiles;
    }

    UPLOADER_KEY = UPLOADER_KEY;

    @Input() operationId: string;
    @Input() currentFolderId: string;
    @Input() uploadButtonTranslationKey: string;
    @Input() breadcrumbs: FolderViewModel[];
    @Input() isButtonDisabled: boolean | undefined = true;
    @Input() modalTitleTranslationKey: string;
    @Input() set parentFolderName(parentFolderName: string) {
        this._parentFolderName = parentFolderName;
    }

    @Input() set selectedFiles(selectedFiles: FileModel[]) {
        if (selectedFiles != null) {
            this._selectedFiles = selectedFiles;
            if (!this.conversionStatusChecked) {
                this.conversionStatusChecked = true;
                this.updateFileStatuesAction$$.next(Guid.create().toString());
            }
        }
    }

    @ViewChild('nameForm') nameForm: NgForm;

    @Output() addFiles = new EventEmitter<FileModel[]>();
    @Output() continue = new EventEmitter<NameAndSelectedFilesContinueInformation>();
    @Output() removeFiles = new EventEmitter<ItemsToBeRemovedOptions<FileModel>>();
    @Output() selectedFilesUpdatedBecauseOfUpload = new EventEmitter<FileModel[]>();
    @Output() closed: EventEmitter<void> = new EventEmitter<void>();
    @Output() dismissed: EventEmitter<void> = new EventEmitter<void>();
    @Output() setNewModalSize = new EventEmitter<string>();

    searchAndSortOptionsForFileSelection: ListSearch = { pageNumber: 1, pageSize: 10, searchQuery: '', sortBy: '' };

    uploader: FileUploader | null;

    blobType = BlobType.UserFile;
    cancelUpload: FileItem;
    cancelAllUploads: { cancel: boolean };
    isMEDprojectEnabled = this.experimentalFeaturesService.isMEDProjectActive;
    breadcrumbString: string;
    isUploadOfFilesEnabled = true;
    isUploadingToTempStorage: boolean | undefined = false;

    rows: TableRow<FileModel>[] = [];
    columns: FileColumn<MEDFileColumnKeys>[] = [
        { key: 'checkbox' },
        { key: 'editStudyID', translationKey: 'GENERAL.MEDCOLUMN.STUDY_ID', defaultValue: '\u2014' },
        { key: 'customTags', translationKey: 'GENERAL.MEDCOLUMN.TAGS', defaultValue: 'MED' },
    ];
    actions: MEDFileAction[] = ['remove', 'editResearchStudyID', 'editTag'];
    targetFolderToBeCreated: CreateFolder;

    private _parentFolderName: string;
    private _selectedFiles: FileModel[] = [];
    private conversionStatusChecked = false;
    private updateFileStatuesAction$$ = new BehaviorSubject('');

    readonly uploadMaxFileSizeInBytes = environment.machineLearningMaxFileSizeInBytes;

    constructor(
        public activeModal: NgbActiveModal,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private fileUploadService: FileUploadService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private fileService: FileService,
    ) {}

    ngAfterViewInit(): void {
        setTimeout((): void => {
            const uploaderInstance = this.fileUploadService.fetchFromCache(UPLOADER_KEY.MY_FILES_UPLOAD_UPLOADER_KEY);
            if (uploaderInstance) {
                this.uploader = uploaderInstance;
                // Listen for upload progress and completion
                this.uploader.onProgressItem = (): void => this.checkIfAllFilesUploaded();
                this.uploader.onCompleteAll = (): void => this.checkIfAllFilesUploaded();
            }
        });
    }

    get isUploading(): boolean {
        return !this.uploader?.queue.length;
    }

    closeModal(): void {
        this.activeModal.close();
        this.closed.emit();
    }

    dismiss(): void {
        this.activeModal.dismiss();
        this.dismissed.emit();
    }

    addedFiles(files: FileModel[]): void {
        this.addFiles.emit(files);
    }

    checkIfAllFilesUploaded(): void {
        if (!this.uploader || !this.uploader.queue.length) {
            this.isUploadingToTempStorage = false;
        } else {
            this.isUploadingToTempStorage = this.uploader.queue.some(file => file.isUploading);
        }
        // Disable the button while uploading
        this.isButtonDisabled = this.isUploadingToTempStorage;
    }

    removeSelectedFiles(): void {
        const removeFilesOptions: ItemsToBeRemovedOptions<FileModel> = { items: this.selectedFiles, shouldConfirmDialogBeOpened: false };
        this.removeFiles.emit(removeFilesOptions);
    }

    uploadFiles(files: FileModel[]): void {
        const payload = this.prepareUploadPayload(files);
        this.activeModal.close();
        this.userNotificationService.showSpinner();

        this.fileUploadService
            .postRdp$(payload)
            .pipe(
                tap((): void => {
                    this.handleUploadSuccess();
                }),
                catchError((error: unknown) => {
                    this.handleUploadError(error);
                    throw error;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    getTitle(): string {
        return this.translateService.instant(this.modalTitleTranslationKey || 'FILE_EXPLORER.MED_TABS.MY_DATA');
    }

    onChangeStudyId(event: { studyId: string; fileIDs: string[] }): void {
        this.selectedFiles = this.selectedFiles.map(file => {
            if (event.fileIDs.includes(file.id)) {
                file.metadata.studyId = event.studyId;
                return file;
            }
            return file;
        });
    }

    onChangeCustomTags(event: { customTags: string[]; fileIDs: string[] }): void {
        this.selectedFiles = this.selectedFiles.map(file => {
            if (event.fileIDs.includes(file.id)) {
                const existingTags = file.metadata.customTags || [];
                const newTags = event.customTags;
                const mergedTags = [...new Set([...existingTags, ...newTags])];
                file.metadata.customTags = mergedTags;
                this.fileService.updateTagsMetadata$(file.id, { tags: mergedTags }).subscribe();
                return file;
            }
            return file;
        });
    }

    onTagRemoved(event: { customTags: string[]; fileIDs: string[] }): void {
        const { customTags, fileIDs } = event;

        this.selectedFiles = this.selectedFiles.map(file => {
            if (fileIDs.includes(file.id)) {
                const updatedTags = file.metadata.customTags.filter(tag => customTags.includes(tag));
                file.metadata.customTags = updatedTags;
                return file;
            }

            return file;
        });
    }

    onSelectedFilesUpdated(files: FileModel[]): void {
        this.selectedFiles = [...files];
        this.selectedFilesUpdatedBecauseOfUpload.emit(this.selectedFiles);
    }

    openSelectionSection(): void {
        this.setNewModalSize.emit('upload-and-select-files');
    }

    buildBreadcrumbString(breadcrumbs: FolderViewModel[]): string {
        this.breadcrumbString = breadcrumbs.map(folder => folder.name || 'Data').join(' > ');
        return this.breadcrumbString;
    }

    private prepareUploadPayload(files: FileModel[]): UploadPayload {
        return {
            folderId: this.currentFolderId,
            files: files.map(file => ({ id: file.id, metadata: { studyId: file.metadata.studyId, tags: file.metadata.customTags || [] } })),
        };
    }

    private handleUploadSuccess(): void {
        this.userNotificationService.hideSpinner();
        this.userNotificationService.showInfo(
            'UPLOAD_MED_FILES_DIALOG.UPLOADED_DATA_DIALOG_TITLE_SUBMITTED_MESSAGE',
            'UPLOAD_MED_FILES_DIALOG.UPLOADED_DATA_DIALOG_TITLE_MESSAGE',
        );
    }

    private handleUploadError(error: unknown): void {
        if (error instanceof HttpErrorResponse && error.error != null) {
            this.userNotificationService.showError(error.error.title as string);
        }

        this.userNotificationService.hideSpinner();
    }
}
